import { FunctionComponent, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  SiteRFPDocumentSummarySortByParam,
  SiteRFPPhaseAPIResponse,
} from '@black-bear-energy/black-bear-energy-common'
import { Box } from '@black-bear-energy/rucksack'
import { useCustomerRFPDocumentSummary } from '../../api/hooks/useCustomerRFPDocumentSummary'
import { useSiteRFPDocumentSummaries } from '../../api/hooks/useSiteRFPDocumentSummaries'
import { DocumentCountField } from './DocumentCountField'
import { SearchableTable } from './SearchableTable'
import { Surface } from '../common/Surface'
import { useDocumentExchangeHeaderBreadcrumbs } from './useDocumentExchangeHeaderBreadcrumbs'

export const CustomerRFPDashboard: FunctionComponent = () => {
  const { customerRFPId } = useParams<{ customerRFPId: string }>()
  const [search, setSearch] = useState('')
  const [requiredMissing, setRequiredMissing] = useState(false)
  const [reviewMissing, setReviewMissing] = useState(false)
  const [phase, setPhase] = useState<SiteRFPPhaseAPIResponse[]>([])
  const [sortBy] = useState(SiteRFPDocumentSummarySortByParam.SiteName)
  const [sortAsc] = useState(true)
  const [page, setPage] = useState(1)
  const { isPending: isPendingCustomerRFP, data: customerRFPData } =
    useCustomerRFPDocumentSummary(parseInt(customerRFPId, 10))
  const { isPending: isPendingSiteRFPs, data } = useSiteRFPDocumentSummaries(
    parseInt(customerRFPId, 10),
    search,
    requiredMissing,
    reviewMissing,
    phase,
    sortBy,
    sortAsc,
    page
  )
  const { records: siteRFPDocumentSummaries, pagination } = data ?? {}
  const totalPages = pagination?.totalPages ?? 1

  const inlineBreadcrumbs = useDocumentExchangeHeaderBreadcrumbs({
    projectBreadcrumbProps: {
      customerRFPName: customerRFPData?.customerRFPName,
      isCustomerRFPNamePending: isPendingCustomerRFP,
    },
  })

  function onFilterChange() {
    setPage(1)
  }

  return (
    <>
      {inlineBreadcrumbs}
      <Surface>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <DocumentCountField
            variant="required"
            value={`${customerRFPData?.required?.numerator}/${customerRFPData?.required?.denominator}`}
            isPending={isPendingCustomerRFP}
          />
          <DocumentCountField
            variant="pendingReview"
            value={customerRFPData?.pendingReview?.toString() ?? '0'}
            isPending={isPendingCustomerRFP}
          />
          <DocumentCountField
            variant="verified"
            value={customerRFPData?.verified?.toString() ?? '0'}
            isPending={isPendingCustomerRFP}
          />
          <DocumentCountField
            variant="additional"
            value={`${customerRFPData?.additional?.numerator}/${customerRFPData?.additional?.denominator}`}
            isPending={isPendingCustomerRFP}
          />
        </Box>
      </Surface>
      <SearchableTable
        variant="siteRFP"
        filterAlign="right"
        records={siteRFPDocumentSummaries}
        isPending={isPendingSiteRFPs}
        search={search}
        setSearch={(search: string) => {
          setSearch(search)
          onFilterChange()
        }}
        requiredMissing={requiredMissing}
        setRequiredMissing={(requiredMissing: boolean) => {
          setRequiredMissing(requiredMissing)
          onFilterChange()
        }}
        reviewMissing={reviewMissing}
        setReviewMissing={(reviewMissing: boolean) => {
          setReviewMissing(reviewMissing)
          onFilterChange()
        }}
        phase={phase}
        setPhase={(phase: SiteRFPPhaseAPIResponse[]) => {
          setPhase(phase)
          onFilterChange()
        }}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
      />
    </>
  )
}
